import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import InputComponent from './InputComponent';
import RecipeListComponent from './RecipeListComponent';
import AdminPanel from './AdminComponent';
import './App.css';
import axios from 'axios';

const MainContent = () => {
    const [fridgeItems, setFridgeItems] = useState([]);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    const [kitchenBasics, setKitchenBasics] = useState(["salt", "pepper", "flour", "baking soda", "oil", "sugar"]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [llamaRecipes, setLlamaRecipes] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const intervalRef = useRef(null);

    // Track user visit on component mount
    useEffect(() => {
        const trackVisit = async () => {
            try {
                await axios.post('/api/track-visit', {
                    referrer: document.referrer,
                    userAgent: navigator.userAgent
                });
            } catch (error) {
                console.error('Error tracking visit:', error);
            }
        };
        trackVisit();
    }, []);

    // Effect to load recipes from backend file on initial mount
    useEffect(() => {
        const loadRecipes = async () => {
            try {
                // console.log('Attempting to load recipes from /api/load-recipes...');
                const response = await axios.get('/api/load-recipes');
                if (Array.isArray(response.data) && response.data.length > 0) {
                    setLlamaRecipes(response.data);
                    setIsSubmitted(true); // Show recipes if loaded
                    // console.log('Loaded recipes from backend file:', response.data);
                } else {
                    // console.log('No recipes found in backend file or file empty.');
                }
            } catch (error) {
                console.error('Failed to load recipes from backend:', error.response ? error.response.data : error.message);
                // Keep this error log for now as it indicates a real problem
            }
        };
        loadRecipes();
    }, []); // Empty dependency array ensures this runs only once on mount

    // Effect to save recipes to backend file whenever llamaRecipes changes
    useEffect(() => {
        const saveRecipes = async () => {
            if (llamaRecipes !== null) { 
                try {
                    // console.log('Attempting to save recipes to /api/save-recipes...', llamaRecipes);
                    await axios.post('/api/save-recipes', { recipes: llamaRecipes });
                    // console.log('Recipes saved successfully to backend file.');
                } catch (error) {
                    console.error('Failed to save recipes to backend:', error.response ? error.response.data : error.message);
                     // Keep this error log for now as it indicates a real problem
                }
            }
        };
        saveRecipes(); 
    }, [llamaRecipes]); // Dependency array ensures this runs when llamaRecipes changes

    const loadingMessages = [
        "Asking the AI chef...",
        "Consulting the recipe oracles...",
        "Mixing ingredients in the cloud...",
        "Preheating the algorithms...",
        "Generating culinary combinations...",
        "Simmering ideas..."
    ];

    const handleDeleteItem = (item) => {
        setFridgeItems(fridgeItems.filter(fridgeItem => fridgeItem !== item));
    };

    const hobbitArr = [{
        "title": "Looks like meats back on the menu, boys",
        "ingredients": [
            "2 hobbits"
        ],
        "instructions": [
            "1. Boil em\n 2. Mash um\n 3. Stick um in a stew"
        ]
     }];

    const updateKitchenBasics = (val) => {
         setKitchenBasics(val)
      }

    const handleAddItem = (item) => {
        setFridgeItems(prevItems => {
            const updatedItems = [...prevItems, item];
            setActiveItemIndex(updatedItems.length - 1);
            return updatedItems;
        });
    };

    const handleSubmit = async () => {
        setLlamaRecipes(null);
        setIsLoading(true);
        setIsSubmitted(true);

        // Start loading message cycle
        setLoadingMessage(loadingMessages[0]);
        let messageIndex = 0;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            messageIndex = (messageIndex + 1) % loadingMessages.length;
            setLoadingMessage(loadingMessages[messageIndex]);
        }, 2500);

        await callAPI();
    };

    const checkDBRecipes = async () => {
        try {
            const response = await axios.post('/recipes/search', {
                fridgeItems
            });

            setLlamaRecipes(response.data);
            console.log(response.data);
            return response.data;
        } catch (error) {   
            console.log(error.response ? error.response.data : 'Failed to fetch from DB');
            setIsLoading(false);
        }
    }

    const callAPI = async () => {
        if (fridgeItems.length === 0) {
           setIsLoading(false);
           clearInterval(intervalRef.current);
           intervalRef.current = null;
           return; 
        }

        if (fridgeItems.includes("human meat")){
            setLlamaRecipes(hobbitArr);
            setIsLoading(false);
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return;
        }

        const callHFLlama7b = async () => {
            try {
                const response = await axios.post('/api/recipes/huggingface', { fridgeItems, kitchenBasics });
                setLlamaRecipes(response.data.recipes);
                
                // Track recipe creation
                if (response.data.recipes && response.data.recipes.length > 0) {
                    try {
                        await axios.post('/api/track-recipe-creation');
                    } catch (error) {
                        console.error('Error tracking recipe creation:', error);
                    }
                }
            } catch (error) {
                console.error('Error calling backend API:', error.response ? error.response.data : error.message);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        };
        await callHFLlama7b();
    }

    return (
        <div className="App">
            <header className="app-header">
                <img src="/feastlogo.png" alt="FeastFinder AI Recipe Maker Logo" className="app-logo" />
                <div className="header-controls">
                    <div className="input-section">
                        <InputComponent
                            onAddItem={handleAddItem}
                            onDeleteItem={handleDeleteItem}
                            fridgeItems={fridgeItems}
                            kitchenBasics={kitchenBasics}
                            setKitchenBasics={updateKitchenBasics}
                        />
                    </div>
                    <p className="app-instructions">
                        Welcome to FeastFinder! Enter the ingredients you currently have in your fridge or pantry 
                        one by one using the 'Add' button. You can also remove default kitchen basics 
                        (like salt, pepper) if you don't have them. Once your ingredients are listed, 
                        click 'Get Recipes'. Our AI will generate recipe ideas based <em>only</em> on the items 
                        you've provided. <strong>Important:</strong> These recipes are AI-generated suggestions. 
                        Please review them carefully for accuracy, safety, and completeness before 
                        attempting to cook them.
                    </p>
                    <button onClick={handleSubmit} className="submit-button get-recipes-button">Get Recipes</button>
                </div>
            </header>
            <main className="app-main">
                {isSubmitted && <RecipeListComponent 
                                    llamaRecipes={llamaRecipes} 
                                    isLoading={isLoading} 
                                    loadingMessage={loadingMessage}
                                    activeItemIndex={activeItemIndex} 
                                />}
            </main>
        </div>
    );
};

const AdminRoute = () => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        const checkAdminAccess = async () => {
            try {
                const response = await axios.get('/api/check-admin');
                setIsAuthorized(response.data.authorized);
            } catch (error) {
                setIsAuthorized(false);
            } finally {
                setIsChecking(false);
            }
        };
        checkAdminAccess();
    }, []);

    if (isChecking) {
        return <div>Checking access...</div>;
    }

    return isAuthorized ? <AdminPanel /> : <Navigate to="/" replace />;
};

const App = () => {
    return (
        <div className="app-container">
            <nav className="main-nav">
                <Link to="/" className="nav-link">Home</Link>
            </nav>
            <Routes>
                <Route path="/" element={<MainContent />} />
                <Route path="/admin" element={<AdminRoute />} />
            </Routes>
        </div>
    );
};

export default App;